/**
 * Provider console state, Home and shell copy (ADR-W-E1: one derived
 * provider display state; Home "Needs you now"; state-aware navigation).
 *
 * Follows the quick-print pattern: one English module spread into the main
 * dictionary and one translated module per locale spread into that locale's
 * file, so parallel work on src/i18n.ts and src/i18n/locales/* does not
 * collide on these keys. Every key ships in all supported locales
 * (localization-production-coverage.test.ts).
 */
export const providerConsoleEnglish = {
  // Display state labels (sidebar chip, Home status line).
  "providerState.label.statusUnavailable": "Status unavailable",
  "providerState.label.checkingOffers": "Checking status",
  "providerState.label.settingUp": "Setting up",
  "providerState.label.inReview": "In review",
  "providerState.label.moreInformationRequired": "More information required",
  "providerState.label.rejected": "Not approved",
  "providerState.label.openForJobs": "Open for jobs",
  "providerState.label.pausedByYou": "Paused by you",
  "providerState.label.pausedByTinkr": "Paused by Tinkr",
  "providerState.label.notReceivingOffers": "Not receiving offers",
  "providerState.label.suspended": "Suspended",
  "providerState.label.closed": "Account closed",

  // Full-page state explanations.
  "providerState.unavailable.title": "We couldn't load your account status",
  "providerState.unavailable.body": "Your jobs and settings are safe. Check your connection and try again.",
  "providerState.retry": "Try again",
  "providerState.retrying": "Trying again…",
  "providerState.staleNotice": "We couldn't refresh your account status, so this page may be out of date.",
  "providerState.offersUnconfirmedNotice": "We couldn't confirm whether you're receiving new offers right now.",
  "providerState.review.refreshHint": "Use Refresh at the top of the page to check for a decision.",
  "providerState.suspended.title": "Your provider account is suspended",
  "providerState.suspended.body":
    "While your account is suspended you won't receive new offers and your shop settings are locked. Contact support to find out why and what happens next with your open jobs and payouts.",
  "providerState.closed.title": "This provider account is closed",
  "providerState.closed.body": "It can no longer receive offers or make changes. Contact support if you think this is a mistake.",
  "providerState.hold.accountNote": "Account stays available for your sign-in, data export and account deletion.",
  "providerState.contactSupport": "Contact support",
  "providerState.decision.moreInfo.title": "We need more information",
  "providerState.decision.moreInfo.body":
    "Our review team needs more details before approving your account. Update your details in Setup and submit again. Contact support if you're not sure what to add.",
  "providerState.decision.rejected.title": "Your application wasn't approved",
  "providerState.decision.rejected.body":
    "Our review team couldn't approve your account as submitted. You can update your details in Setup and submit again, or contact support to ask why.",
  "providerState.decision.openSetup": "Open setup",

  // Home: blocked-offers banner.
  "providerHome.sub.notReceiving": "New offers are on hold. Your jobs in progress are below.",
  "providerHome.sub.neutral": "Here's your shop at a glance.",
  "providerHome.blocked.title": "You're not receiving new offers",
  "providerHome.blocked.body": "Keep working on the jobs you already accepted.",
  "providerHome.blocker.pausedByYou": "You paused new offers.",
  "providerHome.blocker.resume": "Resume offers",
  "providerHome.blocker.resuming": "Resuming…",
  "providerHome.blocker.resumeFailed": "We couldn't resume offers. Try again, or use Availability in Operations.",
  "providerHome.blocker.openAvailability": "Open availability settings",
  "providerHome.blocker.pausedByTinkr": "Tinkr paused new offers after {count} of {threshold} strikes in the last {days} days.",
  "providerHome.blocker.lastStrike": "Latest strike: {kind}, {date}",
  "providerHome.blocker.country": "New orders aren't open in your country right now.",
  "providerHome.blocker.agreement": "Accept the current supplier agreement to receive new offers.",
  "providerHome.blocker.profile": "Finish your shop details (shipping and service area) to receive new offers.",
  "providerHome.blocker.review": "Your verification needs attention before you can receive new offers.",

  // Home: "Needs you now".
  "providerHome.needs.title": "Needs you now",
  "providerHome.needs.offerExpiring": "Offer {ref} expires in {time}",
  "providerHome.needs.startOverdue": "Start printing {ref}: was due {date}",
  "providerHome.needs.handoffOverdue": "Hand off {ref}: was due {date}",
  "providerHome.needs.startToday": "Start printing {ref} by {time} today",
  "providerHome.needs.handoffToday": "Hand off {ref} by {time} today",
  "providerHome.needs.incident": "A quality issue on {ref} is waiting for your response",
  "providerHome.needs.payoutOwed": "{amount} is waiting for you. Set up payouts to get paid.",
  "providerHome.needs.payoutSetup": "Set up payouts so you can be paid for your jobs.",
  "providerHome.needs.alertsOff": "Get an alert on this device the moment a new offer arrives.",
  "providerHome.needs.more": "{count} more in the work queue",
  "providerHome.needs.clear": "Nothing needs you right now.",
  "providerHome.needs.incomplete": "Some items couldn't be loaded, so this list may be missing something.",
  "providerHome.action.reviewOffer": "Review",
  "providerHome.action.openJob": "Open job",
  "providerHome.action.respond": "Respond",
  "providerHome.action.setUpPayouts": "Set up payouts",
  "providerHome.action.turnOnAlerts": "Turn on alerts",
  "providerHome.action.dismiss": "Dismiss",
  "providerHome.earnings.payoutSetupNeeded": "Earned · payout setup needed",

  // Home: card errors.
  "providerHome.error.page": "Parts of your dashboard didn't load.",
  "providerHome.error.offers": "Couldn't load your offers.",
  "providerHome.error.jobs": "Couldn't load your jobs.",
  "providerHome.error.earnings": "Couldn't load your earnings.",
  "providerHome.error.quality": "Couldn't load quality issues.",
  "providerHome.error.setup": "Couldn't load your shop setup.",
  "providerHome.error.unavailable": "Tinkr is having trouble right now. Try again in a moment.",
  "providerHome.error.reference": "Reference: {requestId}",

  // Home: shop setup line.
  "providerHome.setup.title": "Your shop",
  "providerHome.setup.complete": "Everything used to match you with offers is set up.",
  "providerHome.setup.missing": "Not set yet: {items}",
  "providerHome.setup.open": "Review shop setup",

  // Shell.
  "providerShell.skipToContent": "Skip to content",
  "providerShell.nav.home": "Home",
  "providerShell.nav.setup": "Setup",
  "providerShell.nav.queue": "Queue",
  "providerShell.nav.menu": "Menu",
  "providerShell.nav.mobileLabel": "Main",
  "providerShell.nav.offersWaiting": "Offers waiting: {count}",
  "providerShell.refreshUpdated": "updated {time}",
  "providerShell.notice.dismiss": "Dismiss notice: {title}",
  "providerShell.account.verification": "Verification & agreement",
  "providerShell.account.verificationBody": "Identity check, business review and the supplier agreement.",
  "providerShell.account.integrationsBody": "Tell us which tools you'd like Tinkr to connect to.",

  // PRC-9: a write the RPC rejects because the provider is not active.
  "apiError.rejection.providerNotMutable":
    "Your account can't make changes right now because it isn't active. Contact support for help."
} as const;

export type ProviderConsoleMessageKey = keyof typeof providerConsoleEnglish;
export type ProviderConsoleMessages = Record<ProviderConsoleMessageKey, string>;
